/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { errorApiRef, useApi } from '@backstage/core-plugin-api'
import { useCallback } from 'react'
import useAsyncRetry from 'react-use/lib/useAsyncRetry'

import { circleCIApiRef } from '../api'
import { useAsyncPolling } from './useAsyncPolling'
import { useProjectSlugFromEntity } from './useBuilds'

const INTERVAL_AMOUNT = 1500

export function useBuildWithSteps(buildId: number) {
  const slug = useProjectSlugFromEntity()
  const api = useApi(circleCIApiRef)
  const errorApi = useApi(errorApiRef)

  const getBuildWithSteps = useCallback(async () => {
    if (!slug.owner || !slug.repo || !slug.vcs) {
      return Promise.reject(new Error('Missing CircleCI annotation on entity'))
    }

    try {
      return await api.getBuild(slug, buildId)
    } catch (e) {
      errorApi.post(e as Error)
      throw e
    }
  }, [slug, buildId, api, errorApi])

  const { loading, value, retry } = useAsyncRetry(() => getBuildWithSteps(), [getBuildWithSteps])

  const { startPolling, stopPolling } = useAsyncPolling(getBuildWithSteps, INTERVAL_AMOUNT)

  return [
    { loading, value, retry },
    {
      getBuildWithSteps,
      startPolling,
      stopPolling,
    },
  ] as const
}
